import { useState, useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { dsEnvelopeService } from '@services/dsEnvelopeService';
import { dsReviewService } from '@services/dsReviewService';
import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import { Textarea } from '@components/ui/Input';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { useTranslation } from '@hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@stores/authStore';
import type { Envelope, EnvelopeStatus } from '@workpay/ds-shared/types/envelope';
import { downloadAllSignedDocuments } from '@utils/downloadEnvelopeDocs';
import { EditAndResendModal, type RetriggerMode } from './EditAndResendModal';
import styles from './SigningRequestActions.module.scss';

interface SigningRequestActionsProps {
  signingRequest: Envelope;
  /** Called when the user picks "Send for Review" from the menu. Parent owns the modal. */
  onSendForReview?: () => void;
  /** Called when the user picks "Audit trail" from the menu. Parent owns the audit modal. */
  onAudit?: () => void;
}

const EDITABLE_STATUSES: EnvelopeStatus[] = [
  'SENT',
  'VIEWED',
  'SIGNING_IN_PROGRESS',
  'REVISED',
  'PENDING_REVIEW',
  'UNDER_NEGOTIATION',
];

const TERMINAL_OR_NON_VOIDABLE: EnvelopeStatus[] = ['COMPLETED', 'VOIDED', 'EXPIRED', 'DECLINED'];

export function SigningRequestActions({ signingRequest, onSendForReview, onAudit }: SigningRequestActionsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [showEditConfirm, setShowEditConfirm] = useState(false);

  const navigateToEdit = useCallback(
    (mode: RetriggerMode) => {
      navigate({
        to: '/w/signing-requests/$requestId/edit',
        params: { requestId: signingRequest.id },
        search: { retriggerMode: mode },
      });
    },
    [navigate, signingRequest.id],
  );

  const isInReview =
    signingRequest.status === 'PENDING_REVIEW' || signingRequest.status === 'UNDER_NEGOTIATION';

  // Same cache key as ReviewStatusPanel so invalidations cross-refresh.
  const { data: reviews } = useQuery({
    queryKey: ['ds-reviews', 'ENVELOPE', signingRequest.id],
    queryFn: () => dsReviewService.getForTarget('ENVELOPE', signingRequest.id),
    enabled: isInReview,
  });
  const activeReview = useMemo(() => {
    if (!reviews || reviews.length === 0) return null;
    return (
      reviews.find((r) => r.status === 'IN_PROGRESS' || r.status === 'PENDING') ?? reviews[0] ?? null
    );
  }, [reviews]);
  const reviewIsActive =
    !!activeReview && (activeReview.status === 'PENDING' || activeReview.status === 'IN_PROGRESS');

  const voidMutation = useMutation({
    mutationFn: (reason: string) => dsEnvelopeService.void(signingRequest.id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dsEnvelope', signingRequest.id] });
      queryClient.invalidateQueries({ queryKey: ['dsEnvelopes'] });
      setShowVoidModal(false);
      setVoidReason('');
      addNotification({
        variant: 'success',
        title: t('K_DS_NOTIF_SIGNING_REQUEST_VOIDED', 'Signing request voided'),
      });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const reviewCancelMutation = useMutation({
    mutationFn: (reviewId: string) => dsReviewService.cancel(reviewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ds-reviews', 'ENVELOPE', signingRequest.id] });
      void queryClient.invalidateQueries({ queryKey: ['dsEnvelope', signingRequest.id] });
      addNotification({ variant: 'success', title: t('K_REVIEW_CANCELLED_SUCCESS', 'Review cancelled successfully.') });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const reviewCompleteMutation = useMutation({
    mutationFn: (reviewId: string) => dsReviewService.complete(reviewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ds-reviews', 'ENVELOPE', signingRequest.id] });
      void queryClient.invalidateQueries({ queryKey: ['dsEnvelope', signingRequest.id] });
      addNotification({ variant: 'success', title: t('K_REVIEW_COMPLETED_SUCCESS', 'Review completed successfully.') });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const reviewAuthorCompleteMutation = useMutation({
    mutationFn: (reviewId: string) => dsReviewService.markAuthorComplete(reviewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ds-reviews', 'ENVELOPE', signingRequest.id] });
      void queryClient.invalidateQueries({ queryKey: ['dsEnvelope', signingRequest.id] });
      addNotification({ variant: 'success', title: t('K_REVIEW_AUTHOR_COMPLETE_SUCCESS', 'Marked as complete.') });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const handleDownloadSigned = useCallback(async () => {
    try {
      await downloadAllSignedDocuments(signingRequest.id, signingRequest.documents);
    } catch {
      addNotification({
        variant: 'critical',
        title: t('K_DS_NOTIF_DOWNLOAD_FAILED', 'Failed to download signed documents'),
      });
    }
  }, [signingRequest.id, signingRequest.documents, addNotification, t]);

  const canVoid =
    hasPermission('ENVELOPES_VOID') && !TERMINAL_OR_NON_VOIDABLE.includes(signingRequest.status);
  const canEdit = hasPermission('ENVELOPES_REVISE') && EDITABLE_STATUSES.includes(signingRequest.status);
  const isDraftLike =
    signingRequest.status === 'DRAFT' || signingRequest.status === 'REVIEW_COMPLETED';
  const canContinueDraft = hasPermission('ENVELOPES_UPDATE') && isDraftLike;
  // Send for Review is intentionally locked to DRAFT only — once a review has
  // completed, the next action is to finalise + send, not start another review.
  const canSendForReview =
    hasPermission('ENVELOPES_CREATE') &&
    signingRequest.status === 'DRAFT' &&
    !!onSendForReview;
  const canDownloadSigned =
    hasPermission('ENVELOPES_DOWNLOAD') && signingRequest.status === 'COMPLETED';
  const hasSignedRecipients = signingRequest.recipients?.some((r) => r.status === 'SIGNED');

  // Plain array — ProfileActionsMenu isn't memoised, so array identity
  // doesn't buy anything. Also avoids pretending the TanStack mutation result
  // objects are stable refs in a dep array.
  const actions: ProfileAction[] = [];

  if (canContinueDraft) {
    actions.push({
      id: 'continue-editing',
      label: t('K_DS_ACTION_CONTINUE_EDITING', 'Continue Editing'),
      variant: 'primary',
      onClick: () => {
        navigate({
          to: '/w/signing-requests/$requestId/edit',
          params: { requestId: signingRequest.id },
          // REVIEW_COMPLETED lands on Step 4 (Place Fields) — author finalises
          // fields after the review parked the envelope.
          search:
            signingRequest.status === 'REVIEW_COMPLETED'
              ? { retriggerMode: 'reset_all', startStep: 4 }
              : { retriggerMode: 'reset_all' },
        });
      },
    });
  }
  if (canSendForReview && onSendForReview) {
    actions.push({
      id: 'send-for-review',
      label: t('K_DS_SEND_FOR_REVIEW', 'Send for Review'),
      onClick: onSendForReview,
    });
  }

  if (isInReview && reviewIsActive && activeReview) {
    if (!activeReview.authorCompleted) {
      actions.push({
        id: 'mark-review-complete',
        label: t('K_BUTTON_MARK_COMPLETE', 'Mark as Complete'),
        variant: 'primary',
        onClick: () => reviewAuthorCompleteMutation.mutate(activeReview.id),
      });
    }
    actions.push({
      id: 'force-complete-review',
      label: t('K_BUTTON_FORCE_COMPLETE', 'Force Complete'),
      onClick: () => reviewCompleteMutation.mutate(activeReview.id),
    });
    actions.push({
      id: 'cancel-review',
      label: t('K_BUTTON_CANCEL_REVIEW', 'Cancel Review'),
      onClick: () => reviewCancelMutation.mutate(activeReview.id),
    });
  }

  if (canEdit) {
    actions.push({
      id: 'edit-and-resend',
      label: t('K_DS_ACTION_EDIT_AND_RESEND', 'Edit & Resend'),
      onClick: () => {
        // Modal is only meaningful when someone has signed — that's the only
        // scenario where the author can pick `continue` vs `reset_all`. Skip
        // the modal otherwise and go straight to the wizard with `reset_all`.
        if (hasSignedRecipients) {
          setShowEditConfirm(true);
        } else {
          navigateToEdit('reset_all');
        }
      },
    });
  }

  if (canDownloadSigned) {
    actions.push({
      id: 'download-signed',
      label: t('K_DS_ACTION_DOWNLOAD_SIGNED', 'Download Signed'),
      onClick: handleDownloadSigned,
    });
  }

  if (canVoid) {
    actions.push({
      id: 'void',
      label: t('K_DS_ACTION_VOID', 'Void'),
      onClick: () => setShowVoidModal(true),
    });
  }

  if (hasPermission('AUDIT_READ') && onAudit) {
    actions.push({
      id: 'audit',
      label: t('K_AUDIT_TITLE', 'Audit trail'),
      onClick: onAudit,
    });
  }

  return (
    <>
      <ProfileActionsMenu actions={actions} />

      <EditAndResendModal
        isOpen={showEditConfirm}
        onClose={() => setShowEditConfirm(false)}
        onConfirm={(mode) => {
          setShowEditConfirm(false);
          navigateToEdit(mode);
        }}
      />

      <Modal
        isOpen={showVoidModal}
        onClose={() => setShowVoidModal(false)}
        title={t('K_DS_MODAL_VOID_SIGNING_REQUEST', 'Void Signing Request')}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowVoidModal(false)}>
              {t('K_CANCEL', 'Cancel')}
            </Button>
            <Button
              variant="danger"
              loading={voidMutation.isPending}
              onClick={() => voidMutation.mutate(voidReason)}
              disabled={!voidReason.trim()}
            >
              {t('K_DS_MODAL_VOID_SIGNING_REQUEST', 'Void Signing Request')}
            </Button>
          </>
        }
      >
        <p className={styles.modalIntro}>
          {t(
            'K_DS_MODAL_VOID_REASON_HINT',
            'Please provide a reason for voiding this signing request. This action cannot be undone.',
          )}
        </p>
        <Textarea
          value={voidReason}
          onChange={(e) => setVoidReason(e.target.value)}
          placeholder={t('K_DS_PLACEHOLDER_VOID_REASON', 'Reason for voiding...')}
          rows={4}
        />
      </Modal>
    </>
  );
}
